import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {Map} from 'immutable';
import classnames from 'classnames';
import {setUpStore} from 'client/framework';
import {confirmChangesModalActions} from './ConfirmChangesModal.actions';
import {confirmChangesModalStore} from './ConfirmChangesModal.store';
import WindowConfirm from 'client/generic/WindowConfirm/WindowConfirm.react';
import windowConfirmActions from 'generic/WindowConfirm/WindowConfirmActions';
import {callTargetedAction} from '../../framework';

import './confirm-changes-modal.scss';

/**
 * ConfirmChangesModal
 *
 * The ConfirmChangesModal accepts two Mandark resource models, oldResource and newResource,
 * and compares them on the basis of a rules prop: an object of resource key names matched to
 * comparator functions. When the user toggles the ConfirmChangesModal, the component will
 * launch a child WindowConfirm modal displaying all the messages returned by reducing over each
 * comparator function in the rules.
 *
 * An example set of rules:
 * @example
  {
    due_date: (oldExam, newExam) => {
      if (newExam.existsOnServer() && newExam.getDueDate().isBefore(oldExam.getDueDate())) {
        return 'Choosing a past due date may cause some students to be marked \"Late\".';
      } else {
        return null;
      }
    }
  }
 */

export class ConfirmChangesModal extends React.Component {
  static propTypes = {
    newResource: ImmutablePropTypes.record,
    oldResource: ImmutablePropTypes.record,
    onConfirm: PropTypes.func,
    rules: PropTypes.objectOf(PropTypes.func).isRequired,
    storeName: PropTypes.string.isRequired,
    windowConfirmStoreName: PropTypes.string.isRequired
  };

  componentDidUpdate() {
    if (!this.getAlerts().isEmpty() && this.getStore().showModal()) {
      callTargetedAction({
        name: windowConfirmActions.SHOW_MODAL,
        targetStore: this.props.windowConfirmStoreName
      });
    } else if (this.getStore().showModal()) {
      callTargetedAction({
        name: confirmChangesModalActions.TOGGLE_MODAL,
        targetStore: this.props.storeName,
        payload: false
      });
      this.props.onConfirm();
    } else {
      return;
    }
  }

  onDismiss = () => {
    callTargetedAction({
      name: confirmChangesModalActions.TOGGLE_MODAL,
      targetStore: this.props.storeName,
      payload: false
    });
  };

  getAlerts() {
    const {rules, newResource, oldResource} = this.props;
    return newResource.getChangeMap().reduce((acc, curr, key) => {
      if (!rules[key]) {
        return acc;
      }
      const comparator = rules[key];
      const result = comparator(oldResource, newResource);
      return acc.update((alert) => (result ? alert.set(key, result) : alert.delete(key)));
    }, Map());
  }

  getAlertJSX() {
    const alerts = this.getAlerts()
      .valueSeq()
      .reduce((acc, value, i) => {
        acc.push(
          <li key={i} className='confirm-changes-modal__list-item'>
            {value}
          </li>
        );
        return acc;
      }, []);
    return (
      <div className='confirm-changes-modal__list-wrapper'>
        <ul
          className={classnames('confirm-changes-modal__list', {
            'confirm-changes-modal__list--unbullet': this.getAlerts().size === 1
          })}
        >
          {alerts}
        </ul>
        <p>Are you sure you want to continue?</p>
      </div>
    );
  }

  getStore() {
    return setUpStore(confirmChangesModalStore, this.props.storeName);
  }

  render() {
    const alert = this.getAlertJSX();
    return (
      <WindowConfirm
        dismissButtonText='No'
        className='confirm-changes-modal'
        confirmButtonText='Yes'
        hideToolbar={true}
        messageHeading='Are you sure you want to continue?'
        message={alert}
        onConfirm={this.props.onConfirm}
        onDismiss={this.onDismiss}
        storeName={this.props.windowConfirmStoreName}
      />
    );
  }
}
